"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeGraph = exports.getEdgesBetweenFeatures = exports.filterGraphByMaxArea = exports.filterGraphByMinDistance = exports.filterGraphByFeature = void 0;
const rbush_1 = __importDefault(require("rbush"));
const turf_1 = require("@turf/turf");
const polygon_distance_1 = require("./polygon-distance");
const unit_conversion_1 = require("./unit-conversion");
/**
 * Returns a copy of the polygonGraph that only has links relating to a single feature
 *
 * @param graph The graph to operate on
 * @param feature Only return edges that contain this feature
 * @returns A new graph that only contains edges containing the supplied feature
 */
function filterGraphByFeature(graph, feature) {
    return {
        ...graph,
        edges: graph.edges.filter((x) => x.featureA === feature || x.featureB === feature),
    };
}
exports.filterGraphByFeature = filterGraphByFeature;
/**
 * Filters the polygonGraph so that it only has links where the link's minDistance field is
 * below the supplied distance.
 *
 * @param graph The graph to operate on
 * @param distance The distance to compare edges to
 * @returns A new graph that only contains edges that are SHORTER than the supplied distance
 */
function filterGraphByMinDistance(graph, distance) {
    return {
        ...graph,
        edges: graph.edges.filter((x) => x.connectionData.distance < distance),
    };
}
exports.filterGraphByMinDistance = filterGraphByMinDistance;
/**
 * Filters the polygonGraph so that it removes links if both features' area is
 * greater than the supplied area.
 *
 * @param graph The graph to operate on
 * @param maxArea The max area the connection graph should allow in square meters
 * @returns A new graph that only contains edges with features that are both not greater than maxArea
 */
function filterGraphByMaxArea(graph, maxArea) {
    return {
        ...graph,
        edges: graph.edges.filter((x) => !((0, turf_1.area)(x.featureA) > maxArea && (0, turf_1.area)(x.featureB) > maxArea)),
    };
}
exports.filterGraphByMaxArea = filterGraphByMaxArea;
/**
 * Returns the edge in the provided graph that links the two supplied features.
 * Returns undefined if it does not exist. If multiple edges link the two features
 * it will return the first edge it encounters
 *
 * @param graph The graph to search for edges in
 * @param featureA One of the features to look for
 * @param featureB The other feature to look form
 * @returns The edge that joins featureA and featureB
 */
function getEdgesBetweenFeatures(graph, featureA, featureB) {
    return graph.edges.filter((edge) => (edge.featureA === featureA && edge.featureB === featureB) ||
        (edge.featureA === featureB && edge.featureB === featureA));
}
exports.getEdgesBetweenFeatures = getEdgesBetweenFeatures;
/**
 * The "broadphase" - generates a list of polygons with overlapping bounding boxes.
 * The bounds of the polygons is inflated by maxEdgeLengthMeters so you can search
 * nearby polygons as well. This function uses an R-tree internally so it's
 * execution time is between O(log_m(Nn)) and O(n).
 *
 * The coordinates of the features is assumed to be in degrees lat/long
 *
 * @param features A collection of features to find the overlaps of.
 * @param maxEdgeLengthMeters Inflate bounding boxes by this distance.
 * @returns An array of graph edges without accurate distances.
 */
function findOverlappingBoundingBoxes(features, maxEdgeLengthMeters) {
    const unmeasuredEdges = [];
    const tree = new rbush_1.default();
    // In order to have only a single iteration through our Polygons we're going to
    // do something a bit strange. We're going to query our R-tree before insertion.
    // We can use the results of this query to tell if this node intersects with anything
    // already in the tree. This immediately gives us possible intersections.
    //
    // The alternative is to insert all the bounding boxes into the R-tree first (via
    // bulk insertion `tree.load`), and then re-iterate and compare. I don't know which is more
    // performant, so if building/querying the tree is the critical section, try the other method.
    features.features.forEach((feature) => {
        const bounds = (0, turf_1.bbox)(feature);
        const [boundInflateX, boundInflateY] = (0, unit_conversion_1.metersToDegrees)(feature.geometry.coordinates[0][0], [
            maxEdgeLengthMeters / 2,
            maxEdgeLengthMeters / 2,
        ]);
        // Inflate the bounding box to ensure we measure accurately up to the max distance
        const treeItem = {
            minX: bounds[0] - boundInflateX,
            minY: bounds[1] - boundInflateY,
            maxX: bounds[2] + boundInflateX,
            maxY: bounds[3] + boundInflateY,
            feature: feature,
        };
        const existingIntersections = tree.search(treeItem);
        const potentialEdges = existingIntersections.map((res) => ({
            featureA: feature,
            featureB: res.feature,
        }));
        unmeasuredEdges.push(...potentialEdges);
        tree.insert(treeItem);
    });
    return unmeasuredEdges;
}
/**
 * Computes a graph containing edges describing the minimum distances between features.
 *
 * This function is guaranteed to find ALL edges where the `distance < maxEdgeLengthMeters`
 * but may also return edges where the `distance > maxEdgeLengthMeters`
 *
 * @param features The turf/geojson FeatureCollection containing the features to measure
 * @param maxEdgeLengthMeters All edges with distances less than this will be found
 * @param unit An override for the maxEdgeLengthMeters unit.
 * @returns A graph of edges linking the features
 */
function computeGraph(features, maxEdgeLengthMeters, unit) {
    // This is very similar to the collision detection stages in a physics engine:
    // we need to query a whole bunch of geometric relations.
    // As such, it has a similar structure: a broadphase to detect
    // bounding-box-intersections, and a narrowphase to detect polygon-on-polygon
    // intersections.
    // The broadphase vastly reduces the number of polygon-on-polygon checks required.
    //
    // Ideally you want to "balance" between these two phases: the algorithm should
    // spend about the same amount of time in each.
    // - Too much time in the narrow phase:
    //      You should split large polygons to reduce the number of overlapping
    //      bounding boxes. Doing this in a way that doesn't affect output
    //      may be non-trivial.
    // - Too much time in the broadphase is unlikely, but:
    //      Consider adding an "even broader phase" that divides the whole space into
    //      clusters.
    // BROADPHASE: find nearby objects
    const unmeasuredEdges = findOverlappingBoundingBoxes(features, maxEdgeLengthMeters);
    // NARROWPHASE: measure the precise distance
    // This is almost certainly the slow part.
    const measuredEdges = unmeasuredEdges.map((unmeasuredEdge) => {
        const distanceData = (0, polygon_distance_1.getClosestDistanceBetweenPolygons)(unmeasuredEdge.featureA, unmeasuredEdge.featureB, maxEdgeLengthMeters, unit || 'meters');
        return {
            featureA: unmeasuredEdge.featureA,
            featureB: unmeasuredEdge.featureB,
            connectionData: distanceData,
        };
    });
    return {
        edges: measuredEdges,
    };
}
exports.computeGraph = computeGraph;
